import { parse } from "yaml";
import { z } from "zod";

/**
 * 広告を差し込む枠。枠を置くのはページのコードなので、IDはコードで定義する
 */
export const AD_SLOTS = [
  "article-sidebar",
  "body-condition-after-result",
  "adoption-after-list",
  "welcome-plan-after-result",
  "nekosei-play-sidebar",
  "nekosei-result",
] as const;
export type AdSlot = (typeof AD_SLOTS)[number];

export interface AdCategory {
  id: string;
  label: string;
}

export interface AdSize {
  width: number;
  height: number;
}

/**
 * ASPが発行する1サイズ分の素材。リンクと計測用の画像はサイズごとに別の値になる
 */
export interface AdCreative {
  size: AdSize;
  linkUrl: string;
  imageUrl: string;
  impressionUrl: string;
}

export interface AdBanner {
  id: string;
  advertiser: string;
  category: string;
  alt: string;
  creatives: readonly AdCreative[];
}

/** 楽天ウェブサービスで取得した書籍の商品。書影からの遷移先も同じ楽天の商品にする */
export interface AdBookRakuten {
  url: string;
  imageUrl: string;
}

/** Amazonと楽天のリンクで紹介する書籍 */
export interface AdBook {
  id: string;
  category: string;
  title: string;
  author: string;
  amazonUrl: string;
  /** 書影と楽天のリンクの取得元。make fetch/ad-books で取得する */
  rakutenItemCode?: string;
  /** 楽天で取得できていない書籍は、書影を出さずAmazonのリンクだけを出す */
  rakuten?: AdBookRakuten;
}

/**
 * 書籍は書影と書名を並べたカードで出すため、この大きさの枠にだけ出す
 */
export const AD_BOOK_SIZE: AdSize = { width: 300, height: 250 };

export interface AdSlotSizes {
  mobile: AdSize;
  desktop: AdSize;
}

export interface AdSlotConfig {
  categories: readonly string[];
  sizes: AdSlotSizes;
}

/**
 * 枠に表示するバナー。スマートフォンとPCのそれぞれで表示する素材を、枠のサイズに合わせて選んである
 */
export interface SlotBanner {
  id: string;
  alt: string;
  mobile: AdCreative;
  desktop: AdCreative;
}

export interface AdConfig {
  categories: readonly AdCategory[];
  banners: readonly AdBanner[];
  books: readonly AdBook[];
  slots: Readonly<Record<AdSlot, AdSlotConfig>>;
}

export class AdConfigError extends Error {
  constructor(reason: string) {
    super(`広告の設定を解析できませんでした: ${reason}`);
    this.name = "AdConfigError";
  }
}

const AD_ID_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;

const AdIdSchema = z
  .string()
  .regex(AD_ID_PATTERN, "IDは小文字英数字とハイフンのみです");

const AdSizeSchema = z
  .string()
  .regex(/^[1-9][0-9]*x[1-9][0-9]*$/, "サイズは「幅x高さ」で書きます")
  .transform((size): AdSize => {
    const [width, height] = size.split("x").map(Number);
    return { width, height };
  });

const AdSlotConfigSchema = z.object({
  categories: z.array(AdIdSchema),
  sizes: z.object({ mobile: AdSizeSchema, desktop: AdSizeSchema }),
});

const AdConfigSchema = z
  .object({
    categories: z.array(z.object({ id: AdIdSchema, label: z.string().min(1) })),
    banners: z.array(
      z.object({
        id: AdIdSchema,
        advertiser: z.string().min(1),
        category: AdIdSchema,
        alt: z.string().min(1),
        creatives: z
          .array(
            z.object({
              size: AdSizeSchema,
              linkUrl: z.string().url(),
              imageUrl: z.string().url(),
              impressionUrl: z.string().url(),
            }),
          )
          .min(1),
      }),
    ),
    books: z.array(
      z.object({
        id: AdIdSchema,
        category: AdIdSchema,
        title: z.string().min(1),
        author: z.string().min(1),
        amazonUrl: z.string().url(),
        rakutenItemCode: z.string().min(1).optional(),
      }),
    ),
    slots: z.strictObject(
      Object.fromEntries(
        AD_SLOTS.map((slot) => [slot, AdSlotConfigSchema]),
      ) as Record<AdSlot, typeof AdSlotConfigSchema>,
    ),
  })
  .superRefine((config, ctx) => {
    const categoryIds = config.categories.map((category) => category.id);
    for (const duplicated of findDuplicates(categoryIds)) {
      ctx.addIssue({
        code: "custom",
        message: `categoriesのidが重複しています: ${duplicated}`,
      });
    }
    for (const duplicated of findDuplicates(
      [...config.banners, ...config.books].map((ad) => ad.id),
    )) {
      ctx.addIssue({
        code: "custom",
        message: `bannersとbooksのidが重複しています: ${duplicated}`,
      });
    }

    const knownCategoryIds = new Set(categoryIds);
    for (const banner of config.banners) {
      if (!knownCategoryIds.has(banner.category)) {
        ctx.addIssue({
          code: "custom",
          message: `バナー「${banner.id}」のcategoryがcategoriesにありません: ${banner.category}`,
        });
      }
      for (const duplicated of findDuplicates(
        banner.creatives.map((creative) => formatAdSize(creative.size)),
      )) {
        ctx.addIssue({
          code: "custom",
          message: `バナー「${banner.id}」のcreativesでサイズが重複しています: ${duplicated}`,
        });
      }
    }
    for (const book of config.books) {
      if (!knownCategoryIds.has(book.category)) {
        ctx.addIssue({
          code: "custom",
          message: `書籍「${book.id}」のcategoryがcategoriesにありません: ${book.category}`,
        });
      }
    }
    for (const slot of AD_SLOTS) {
      for (const category of config.slots[slot].categories) {
        if (!knownCategoryIds.has(category)) {
          ctx.addIssue({
            code: "custom",
            message: `枠「${slot}」のcategoriesにcategoriesに無いidがあります: ${category}`,
          });
        }
      }
      if (
        config.slots[slot].categories.length > 0 &&
        selectSlotBanners(config, slot).length === 0 &&
        selectSlotBooks(config, slot).length === 0
      ) {
        ctx.addIssue({
          code: "custom",
          message: `枠「${slot}」のサイズとカテゴリに合うバナー・書籍がありません`,
        });
      }
    }
  });

function formatAdSize(size: AdSize): string {
  return `${size.width}x${size.height}`;
}

function isSameSize(a: AdSize, b: AdSize): boolean {
  return a.width === b.width && a.height === b.height;
}

function findCreative(banner: AdBanner, size: AdSize): AdCreative | undefined {
  return banner.creatives.find((creative) => isSameSize(creative.size, size));
}

function findDuplicates(values: readonly string[]): string[] {
  return [
    ...new Set(
      values.filter((value, index) => values.indexOf(value) !== index),
    ),
  ];
}

export function parseAdConfig(source: string): AdConfig {
  const result = AdConfigSchema.safeParse(parse(source));
  if (!result.success) {
    throw new AdConfigError(z.prettifyError(result.error));
  }
  return result.data;
}

export function adCategoryIdsOf(config: AdConfig): string[] {
  return config.categories.map((category) => category.id);
}

/**
 * 枠に表示するバナーを、設定ファイルでの並び順のまま返す。枠のサイズの素材が無いバナーは出さない
 * @param overrideCategories ページごとに枠の既定カテゴリを上書きするときに指定する。空配列なら広告を出さない
 */
export function selectSlotBanners(
  config: Pick<AdConfig, "banners" | "slots">,
  slot: AdSlot,
  overrideCategories?: readonly string[],
): SlotBanner[] {
  const { categories: defaultCategories, sizes } = config.slots[slot];
  const categories = new Set(overrideCategories ?? defaultCategories);
  return config.banners.flatMap((banner) => {
    if (!categories.has(banner.category)) {
      return [];
    }
    const mobile = findCreative(banner, sizes.mobile);
    const desktop = findCreative(banner, sizes.desktop);
    return mobile && desktop
      ? [{ id: banner.id, alt: banner.alt, mobile, desktop }]
      : [];
  });
}

/**
 * 枠に表示する書籍を、設定ファイルでの並び順のまま返す。書籍のカードが収まらない大きさの枠には出さない
 * @param overrideCategories ページごとに枠の既定カテゴリを上書きするときに指定する。空配列なら広告を出さない
 */
export function selectSlotBooks(
  config: Pick<AdConfig, "books" | "slots">,
  slot: AdSlot,
  overrideCategories?: readonly string[],
): AdBook[] {
  const { categories: defaultCategories, sizes } = config.slots[slot];
  if (
    !isSameSize(sizes.mobile, AD_BOOK_SIZE) ||
    !isSameSize(sizes.desktop, AD_BOOK_SIZE)
  ) {
    return [];
  }
  const categories = new Set(overrideCategories ?? defaultCategories);
  return config.books.filter((book) => categories.has(book.category));
}

const AdBookRakutenListingsSchema = z.object({
  books: z.array(
    z.object({
      id: z.string(),
      itemCode: z.string(),
      rakuten: z
        .object({ url: z.string().url(), imageUrl: z.string().url() })
        .optional(),
    }),
  ),
});

/** make fetch/ad-books で楽天から取得した書籍の商品 */
export type AdBookRakutenListings = z.infer<typeof AdBookRakutenListingsSchema>;

export function parseAdBookRakutenListings(
  json: unknown,
): AdBookRakutenListings {
  return AdBookRakutenListingsSchema.parse(json);
}

/**
 * 書籍に楽天の商品を結びつける。
 * 設定のrakutenItemCodeを変えたあと取得し直していない商品は、別の本を指しうるので使わない
 */
export function withAdBookRakutenListings(
  config: AdConfig,
  listings: AdBookRakutenListings,
): AdConfig {
  return {
    ...config,
    books: config.books.map((book) => {
      const listing = listings.books.find(
        (candidate) =>
          candidate.id === book.id &&
          candidate.itemCode === book.rakutenItemCode,
      );
      return listing?.rakuten ? { ...book, rakuten: listing.rakuten } : book;
    }),
  };
}
